import React, { useRef, useEffect } from 'react';
import './CalculusOfMeaning.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function LivingIntegral() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted loop playsInline className="calculus-hero-video">
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Healthcare &middot; Dynamical Systems &middot; First Principles</span>
          <h1>The Living Integral</h1>
          <p className="calculus-hero-sub">Healthcare data isn't fixed. It never was. The industry just built AI that pretends it is.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* Opening */}
          <section className="calc-section reveal">
            <p className="calc-lead">
              A patient walks into a clinic. The AI reviews their chart: blood panel, symptom list, medication
              history. It produces a classification. At risk. Not at risk. Refer. Don't refer.
            </p>
            <p>
              That classification is based on a <strong>snapshot</strong>. A single moment in time. A photograph
              of a system that has never once in its biological history held still.
            </p>
            <p>
              This is how every major AI system in healthcare operates today.
              It is fundamentally wrong.
            </p>
          </section>

          <Pulse />

          {/* The Core Insight */}
          <section className="calc-section reveal">
            <h2>Health Is Not a Data Point</h2>
            <p>
              Health is not a reading. It is not a score. It is not a label assigned at a moment in time.
            </p>
            <p>
              Health is the <strong>area under the curve</strong> of every event, every medication, every stressor,
              every meal, every sleepless night, every emotional trauma, integrated over the full span of a
              patient's timeline, weighted by how much each event still matters right now.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Core Principle</span>
              <code><strong>Health</strong>(t) = &int; f(stimuli, chemistry, time) dt</code>
              <span className="calc-eq-desc">A patient's state at any moment is the integral of everything that came before, weighted by biological decay.</span>
            </div>

            <p>
              Pharmacology has understood this for decades. The AUC of a drug in the bloodstream determines its
              efficacy, not the peak concentration at any single draw. A single cortisol reading tells you
              almost nothing. The cortisol curve over six months, weighted by its 90-minute half-life and its
              antagonistic relationship with oxytocin, tells you everything.
            </p>
          </section>

          <Pulse />

          {/* What the Industry Gets Wrong */}
          <section className="calc-section reveal">
            <h2>The Snapshot Fallacy</h2>
            <p>
              Every black box AI in healthcare commits the same fundamental error: it treats the patient as a
              photograph when the patient is a film.
            </p>

            <div className="calc-attempts">
              <div className="calc-attempt">
                <h4>Diagnostic AI</h4>
                <p>Takes an image or a lab panel. Classifies it. No temporal context. No memory of what came before. No decay dynamics. A melanoma classifier doesn't know if the lesion has been growing for two weeks or two years.</p>
              </div>
              <div className="calc-attempt">
                <h4>Clinical Decision Support</h4>
                <p>Reviews current vitals and medications. Suggests actions. Doesn't model the cumulative load of chronic stress, sleep deprivation, or emotional trauma that shapes how the body responds to treatment.</p>
              </div>
              <div className="calc-attempt">
                <h4>Risk Prediction</h4>
                <p>Assigns probability of future events based on current features. The features are static. The patient is not. A risk score computed today is stale by tomorrow because the integral has changed.</p>
              </div>
              <div className="calc-attempt">
                <h4>Mental Health Screening</h4>
                <p>A PHQ-9 score is a snapshot of the last two weeks. Depression is a trajectory spanning months. The screening instrument captures a single point on a curve it cannot see.</p>
              </div>
            </div>

            <div className="calc-thesis-box">
              <p>Any system that treats health data as fixed is discarding the integral and keeping only the last data point. That is not a simplification. It is a fundamental misrepresentation of the underlying biology.</p>
            </div>
          </section>

          <Pulse />

          {/* What the Integral Requires */}
          <section className="calc-section reveal">
            <h2>What Computing the Integral Requires</h2>
            <p>
              You cannot compute the integral with a transformer. A transformer processes a context window. It
              does not maintain continuous state with biological decay dynamics. It does not model antagonistic
              pair suppression. It does not track pharmacokinetic half-lives.
            </p>
            <p>
              The integral requires an architecture that was <em>designed</em> to evolve state over time.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Pharmacokinetic Decay</span>
              <code>ds&#7522;/dt = &minus;&lambda;&#7522; &middot; (s&#7522; &minus; E&#7522;) + Stimulus&#7522;(t)</code>
              <span className="calc-eq-desc">Each neurochemical decays toward equilibrium with a unique rate constant derived from published biological half-lives. Adrenaline: 2 min. Dopamine: 20 min. Cortisol: 90 min. Serotonin: 360 min.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Antagonistic Pair Suppression</span>
              <code>Cortisol &uarr; &rArr; Oxytocin &darr; &nbsp;&nbsp; Dopamine &uarr; &rArr; Serotonin &darr;</code>
              <span className="calc-eq-desc">These are not correlations learned from data. They are biological constraints. When cortisol rises, oxytocin is actively suppressed. The integral must respect these dynamics or it is not computing biology.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Hodgkin-Huxley Conductance Gating</span>
              <code>g(S) = m<sub>&infin;</sub>(S) &middot; h<sub>&infin;</sub>(S)</code>
              <span className="calc-eq-desc">Every gate in the NCA uses the same class of equations that govern ion channels in biological neurons. No sigmoid. No softmax. The computation IS the biology.</span>
            </div>

            <p>
              The Neurochemical Conductance Architecture maintains a continuous state vector. Each of its 34
              heads carries a value that evolves through five biological feedback mechanisms: mean-reverting
              decay, autoreceptor negative feedback, receptor desensitization, vesicle pool depletion, and
              concentration-dependent clearance.
            </p>
            <p>
              The state at time T is the integral of every stimulus that came before it, weighted by the
              biological dynamics that govern how each stimulus persists, interacts, and decays.
            </p>
            <p>
              That is the integral. And that is what healthcare AI must compute.
            </p>
          </section>

          <Pulse />

          {/* From Hz to Hormones */}
          <section className="calc-section reveal">
            <h2>From Hz to Hormones</h2>
            <p>
              We have demonstrated that raw voice audio, processed through the NCA, produces biologically
              correct neurochemical state changes in real time. A stressed voice raises cortisol and
              norepinephrine, lowers serotonin and GABA. A calm voice reverses it. Every state change traces
              through the conductance cascade to the specific acoustic features that caused it.
            </p>
            <p>
              No transcription. No sentiment analysis. No black box. The frequency of a human voice,
              transduced through biological mechanics, producing an auditable neurochemical state.
            </p>
            <p>
              This is what healthcare AI looks like when you respect the integral.
            </p>
          </section>

          <Pulse />

          {/* The Standard */}
          <section className="calc-section reveal">
            <h2>The Audit Question</h2>
            <p>
              In fitness apps, an unexplainable output is an inconvenience. In clinical decision support, it
              is a contraindication.
            </p>
            <p>
              The FDA does not ask "what did your model decide?" The FDA asks <em>"why did it decide that, and
              can we verify the mechanism independently of the model's self-report?"</em>
            </p>

            <div className="calc-thesis-box">
              <p>Can your model show its work without speaking? If the only way your model can explain itself is by generating language about its decision, you are auditing a narrative, not a mechanism. The NCA's 176-byte state vector IS the explanation. No words required.</p>
            </div>

            <p>
              Every byte maps to a named biological mechanism. Every mechanism traces through a conductance gate
              with Hodgkin-Huxley dynamics. That is not interpretability added after the fact. That is
              interpretability as architecture.
            </p>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section reveal">
            <h2>The Question</h2>
            <p>
              Every AI company in healthcare is building faster ways to classify snapshots.
            </p>
            <p>
              We are building the architecture that computes the integral.
            </p>
            <p>
              The patient is not a photograph. The patient is a curve. And the only system worth building for
              life-or-death decisions is one that respects the mathematics of how biology actually works.
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Living Integral</span>
              <code><strong>Health</strong>(t) = &int; f(stimuli, chemistry, time) dt</code>
              <span className="calc-eq-desc">This is not a metaphor. This is the operating equation.</span>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}

export default LivingIntegral;
